/**
 * Every string the account register renders.
 *
 * The wording is transcribed from the UI/UX pack. What is **not** here is as
 * load-bearing as what is: no sentence explains why the page carries one filter
 * and not three, no sentence names a setting, and no sentence restates a
 * mechanic the screen performs — the operator needs to be able to act
 * differently after reading a line, and none of those would let them.
 */
export const copy = {
  crumb: 'المستخدمون',

  head: {
    title: 'المستخدمون',
    lede: 'كلّ حساب على المنصّة، مع دوره وحالته. من هنا يُرشَّح حاملو الأدوار ويُحظر الحساب أو يُرفع حظره، ومن كلّ صفّ طريقٌ إلى ملفّ صاحبه.',
  },

  section: {
    heading: 'قائمة الحسابات',
  },

  /** The register's own lookup — served by `backend-auth`'s `query` over name, handle and number. */
  search: {
    label: 'ابحث في الحسابات',
    placeholder: 'بالاسم أو المعرّف أو الرقم…',
    hint: 'البحث يجري على كامل السجلّ، لا على الصفحة المعروضة وحدها.',
  },

  filter: {
    legend: 'ترشيح القائمة بالدور',
    label: 'الدور',
    all: 'كلّ الأدوار',
    apply: 'طبّق',
    reset: 'امسح المرشّح',
  },

  caption: 'حسابات المنصّة بدورها وحالتها',

  columns: {
    user: 'المستخدم',
    phone: 'رقم الهاتف',
    roles: 'الأدوار',
    state: 'حالة الحساب',
    actions: 'أفعال الحساب',
  },

  /**
   * The roster is the one surface where the mobile number may be absent from the
   * payload while the column stands. The cell says which it is instead of
   * drawing a dash that would read as «this account has no number» — the number
   * is the account's sole credential, so no account can be without one.
   */
  phoneUnavailable: 'يُقرأ من ملفّ الحساب',

  rowMenu: {
    label: (name: string) => `أفعال حساب «${name}»`,
    open: 'افتح ملفّ الحساب',
  },

  /**
   * The traversal control. `backend-auth` pages the register by offset and
   * returns the total it drew from, so this pager states both figures — the page
   * of how many, and the rows of how many.
   */
  pager: {
    label: 'صفحات الحسابات',
    shownOne: 'حساب واحد معروض',
    shownTwo: 'حسابان معروضان',
    shownFew: 'حسابات معروضة',
    shownMany: 'حساباً معروضاً',
  },

  states: {
    loading: 'جارٍ تحميل الحسابات',
    /** The filter matched nothing — the block carries the control that clears it. */
    emptyFiltered: 'لا حساب يحمل هذا الدور.',
    /** The lookup matched nothing — names the term back, so the reader sees what was searched. */
    emptySearched: (term: string) => `لا حساب يطابق «${term}».`,
    emptyFilteredAction: 'اعرض كلّ الأدوار',
    /** No filter, and nothing to show. A register with no accounts is not a good state. */
    empty: 'لا حساب على المنصّة بعد.',
    /**
     * A page walked past the end — not an empty register, and not a filter that
     * matched nothing. Each of the three has its own way out, so none may borrow
     * another's sentence.
     */
    emptyPage: 'لا حساب في هذه الصفحة. عد إلى السابقة.',
  },
} as const;
